import React, { FC, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";

import { getApplications } from "../../apis/get-applications";
import { useLayout } from "../../hooks/use-layout";
import { Application } from "../../models/application";

import {
  Alignment,
  Button,
  Dialog,
  FaIcon,
  FaIcons,
  LayoutGrid,
  Section,
  Select,
  Spinner,
  Tabs,
  useMediaQuery
} from "@in/component-library";
import { AppContext } from "../../../app-context";
import ApplicationTable from "../../components/application-table/application-table";
import Page from "../../components/page/page";

export interface ActionModalInfo {
  show: boolean;
  title: string;
  content: ReactNode;
}

export interface ActionModalConfirmation {
  open: boolean;
  title: string;
  confirmText: string;
  cancelText: string;
  text: string;
  onConfirm: () => void;
  isProcessing: boolean;
}

interface Tab {
  ["aria-label"]: string;
  ["aria-selected"]: boolean;
  ["aria-controls"]: string;
}

export enum CategoryType {
  Inquiry = "Inquiry",
  Application = "Application"

  // Todo: Uncomment when implemented
  // PaymentRequest = "PaymentRequest",
  // FinalReport = "FinalReport",
}

export interface ApplicationWithCategoryType extends Application {
  categoryType: CategoryType;
}

const Applications: FC = () => {
  const { account } = useContext(AppContext);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [selectedOption, setSelectedOption] = useState<string>("all");
  const { translations } = useLayout();
  const {
    data,
    error,
    mutate: refetchApplications
  } = useSWR([`applications`, account], ([url]) => getApplications(url), {
    refreshInterval: 5 * 60 * 1000
  });

  const [actionModalInfo, setActionModalInfo] = useState<ActionModalInfo>({
    show: false,
    title: "",
    content: ""
  });

  const [actionModalConfirmation, setActionModalConfirmation] = useState<ActionModalConfirmation>({
    open: false,
    title: "",
    confirmText: "",
    cancelText: "",
    text: "",
    onConfirm: () => {
      console.log("empty callback");
    },
    isProcessing: false
  });

  const handleActionModalInfoOnClose = () => {
    setActionModalInfo((prevState) => {
      return {
        ...prevState,
        show: false
      };
    });
  };

  const handleConfirmationBoxOnConfirm = () => {
    if (actionModalConfirmation.isProcessing) return;

    actionModalConfirmation.onConfirm();
    setActionModalConfirmation((prevState) => {
      return {
        ...prevState,
        open: prevState.isProcessing
      };
    });
  };

  const handleConfirmationBoxOnCancel = () => {
    setActionModalConfirmation((prevState) => {
      return {
        ...prevState,
        open: false
      };
    });
  };

  const [isMobile, setIsMobile] = useState<boolean>();

  const mediaQueryMobile = useMediaQuery("(max-width: 48em)");
  const mobileMatch = mediaQueryMobile ? mediaQueryMobile.matches : undefined;

  // Manage mobile view
  useEffect(() => {
    if (mobileMatch !== undefined && mobileMatch !== isMobile) {
      setIsMobile(mobileMatch);
    }
  }, [isMobile, mobileMatch]);

  const categoryTypes: CategoryType[] = useMemo(() => Object.values(CategoryType), []);

  const applications: ApplicationWithCategoryType[][] = useMemo(() => {
    if (!data) {
      return [[], ...categoryTypes.map(() => [])];
    }

    // Add category type to applications object
    const updatedApplications = data.map((application) => {
      let categoryType;
      const title = application.title ? application.title : translations.applicationsTitleUnnamed;

      // Get category type from formType
      switch (application.formType) {
        case "Application":
          categoryType = CategoryType.Application;
          break;
        default:
          categoryType = CategoryType.Inquiry;
          break;
      }

      return { ...application, categoryType, title };
    });

    const applicationsCatgorizedByType = categoryTypes.map((categoryType) =>
      updatedApplications.filter((application) => application.categoryType === categoryType)
    );

    return [updatedApplications, ...applicationsCatgorizedByType];
  }, [data, translations, categoryTypes]);

  const tabTitles = [
    translations.applicationsTabTitleAll,
    ...categoryTypes.map((type) => {
      switch (type) {
        case CategoryType.Application:
          return translations.applicationsTabTitleApplications;
        case CategoryType.Inquiry:
          return translations.applicationsTabTitleInquiries;
        default:
          return type;
      }
    })
  ];

  const selectOptions = [
    { text: translations.applicationsTabTitleAll, value: "all" },
    ...categoryTypes.map((type) => {
      switch (type) {
        case CategoryType.Application:
          return {
            text: translations.applicationsTabTitleApplications,
            value: "applications"
          };
        case CategoryType.Inquiry:
          return {
            text: translations.applicationsTabTitleInquiries,
            value: "inquiries"
          };
        default:
          return {
            text: translations.applicationsTabTitleInquiries,
            value: "inquiries"
          };
      }
    })
  ].map((option, i) => {
    return {
      ...option,
      text: `${option.text} (${applications[i].length})`
    };
  });

  const tabs: Tab[] = tabTitles.map((title, i) => {
    return {
      "aria-label": `${title} (${applications[i].length})`,
      "aria-selected": i === activeTab,
      "aria-controls": "panel-" + (i + 1)
    };
  });

  const tabpanels = applications.map((applications, i) => {
    return (
      <div key={i} id={"panel-" + (i + 1)} role="tabpanel" hidden={activeTab !== i}>
        <ApplicationTable
          applications={applications}
          setActionModalInfo={setActionModalInfo}
          setActionModalConfirmation={setActionModalConfirmation}
          caption={tabTitles[i]}
          id={"table-" + i}
          refetchApplications={refetchApplications}
        />
      </div>
    );
  });

  useEffect(() => {
    if (translations.applicationTitle && translations.applicationsHeader && translations.companyName) {
      document.title = `${translations.applicationsHeader} - ${translations.applicationTitle} - ${translations.companyName}`;
    }
  }, [translations]);

  const displayApplicationsDesktop = (
    <>
      <Tabs className="applications__tabs" tabs={tabs} setActiveTab={(focusPosition) => setActiveTab(focusPosition)} />
      <div className="applications__tabpanels-wrapper">{tabpanels}</div>
    </>
  );

  const displayApplicationsMobile = (
    <>
      <Select
        name="Kategori"
        options={selectOptions}
        label="Kategori"
        onChange={(value) => setSelectedOption(value)}
        value={selectedOption}
      />
      <div aria-live="polite">
        {applications.map((applications, i) => {
          return (
            <div key={i} hidden={selectedOption !== selectOptions[i].value}>
              <ApplicationTable
                applications={applications}
                setActionModalInfo={setActionModalInfo}
                setActionModalConfirmation={setActionModalConfirmation}
                caption={tabTitles[i]}
                id={"table-" + i}
                refetchApplications={refetchApplications}
              />
            </div>
          );
        })}
      </div>
    </>
  );
  const actionModalInfoRef = useRef<HTMLDialogElement>(null);
  const actionModalConfirmationRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (actionModalInfo.show) {
      actionModalInfoRef?.current?.showModal();
    } else {
      actionModalInfoRef?.current?.close();
    }
  }, [actionModalInfo]);

  useEffect(() => {
    if (actionModalConfirmation.open) {
      actionModalConfirmationRef?.current?.showModal();
    } else {
      actionModalConfirmationRef?.current?.close();
    }
  }, [actionModalConfirmation]);

  return (
    <Page inputs={[data]} errors={[error]}>
      <Dialog
        title={actionModalInfo.title}
        ref={actionModalInfoRef}
        onClose={handleActionModalInfoOnClose}
        verticalAlignment={isMobile ? Alignment.Bottom : Alignment.Center}
        headerClassName="applications__modal-header"
        closeButton={{
          onClick: () => actionModalInfoRef.current?.close(),
          "aria-label": translations.close
        }}
      >
        <div className="applications__modal-content">
          {actionModalInfo.content}
          <div className="applications__modal-button-group applications__modal-button-group--info">
            <Button theme="neutral" variant="solid" onClick={handleActionModalInfoOnClose}>
              {translations.close}
            </Button>
          </div>
        </div>
      </Dialog>
      <Dialog
        headerClassName="applications__modal-header"
        title={actionModalConfirmation.title}
        ref={actionModalConfirmationRef}
        onClose={handleConfirmationBoxOnCancel}
        verticalAlignment={isMobile ? Alignment.Bottom : Alignment.Center}
      >
        <div className="applications__modal-content">
          <p>{actionModalConfirmation.text}</p>
          <div className="applications__modal-button-group applications__modal-button-group--confirmation">
            <Button theme="neutral" variant="solid" onClick={handleConfirmationBoxOnConfirm}>
              {actionModalConfirmation.isProcessing ? <Spinner /> : actionModalConfirmation.confirmText}
            </Button>
            <Button
              theme="neutral"
              variant="outlined"
              onClick={handleConfirmationBoxOnCancel}
              disabled={actionModalConfirmation.isProcessing}
            >
              {actionModalConfirmation.cancelText}
            </Button>
            <div aria-live="polite" className="applications__modal-live-region">
              {actionModalConfirmation.isProcessing ? translations.processingRequest : null}
            </div>
          </div>
        </div>
      </Dialog>
      <Section className="applications">
        <h1>{translations.applicationsHeader}</h1>
        <div
          style={{ marginTop: "1rem" }}
          dangerouslySetInnerHTML={{
            __html: translations.applicationsHelpText
          }}
        />
        <LayoutGrid className="applications__wrapper">
          <div className="applications__content">
            {applications[0].length > 0 ? (
              isMobile ? (
                displayApplicationsMobile
              ) : (
                displayApplicationsDesktop
              )
            ) : (
              <div className="applications__text-block">
                <FaIcon name={FaIcons.FolderOpenLight} size="2x" />
                <h2 className="applications__text-block-heading">{translations.applicationsEmptyListTitle}</h2>
                <Button
                  className="applications__text-block-button"
                  href="/"
                  rel="noopner"
                  iconPosition="right"
                  iconName={FaIcons.ArrowRight}
                  variant="solid"
                  theme="neutral"
                >
                  {translations.goToHomepage}
                </Button>
              </div>
            )}
          </div>
        </LayoutGrid>
      </Section>
    </Page>
  );
};

export default Applications;